'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname } from 'next/navigation'

function readCsrfCookie(): string | null {
  const m = document.cookie.match(/(?:^|; )__Host-buyer\.csrf=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

// Inline composer for posting a new top-level comment on a listing. Lives
// at the bottom of the comments section. Buyers must be signed in via the
// SMS-OTP cookie flow; signed-out visitors see a sign-in CTA instead.
export function CommentForm({
  listingId,
  signedIn,
}: {
  listingId: string
  signedIn: boolean
}) {
  const [body, setBody] = useState('')
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [posted, setPosted] = useState(false)
  const pathname = usePathname()

  if (!signedIn) {
    return (
      <div className="bg-navy-2 border border-[rgba(255,255,255,0.07)] rounded p-4 text-center text-xs text-text-3">
        <Link
          href={`/auth/buyer-signin?next=${encodeURIComponent(pathname)}`}
          className="text-cyan hover:underline"
        >
          Sign in
        </Link>{' '}
        to ask a public question on this listing.
      </div>
    )
  }

  if (posted) {
    return (
      <div className="bg-green/10 border border-green/30 rounded p-4 text-center text-sm text-green">
        ✓ Comment posted. Refresh to see it appear.
      </div>
    )
  }

  async function post(e?: React.FormEvent) {
    e?.preventDefault()
    const text = body.trim()
    if (!text || posting) return
    setPosting(true)
    setError(null)
    const csrf = readCsrfCookie()
    if (!csrf) {
      setError('Your session expired. Please sign in again.')
      setPosting(false)
      return
    }
    try {
      const res = await fetch(`/api/v1/marketplace/listings/${listingId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ body: text }),
      })
      const data = (await res.json().catch(() => null)) as { error?: string } | null
      if (!res.ok) {
        setError(data?.error ?? 'Could not post comment.')
        return
      }
      setPosted(true)
      setBody('')
    } catch {
      setError('Network error. Try again.')
    } finally {
      setPosting(false)
    }
  }

  return (
    <form onSubmit={post} className="bg-navy-2 border border-[rgba(255,255,255,0.07)] rounded p-3 space-y-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={4000}
        placeholder="Ask a question publicly. Vendors and other buyers can reply."
        disabled={posting}
        className="w-full bg-navy-3 border border-[rgba(255,255,255,0.07)] rounded px-3 py-2 text-sm placeholder:text-text-3 focus:border-cyan/40 outline-none resize-y"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post()
        }}
      />
      {error && (
        <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-3 py-2">
          {error}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-text-3">
          {body.length}/4000 · Use Cmd/Ctrl+Enter to post
        </span>
        <button
          type="submit"
          disabled={posting || !body.trim()}
          className="px-4 py-1.5 bg-white text-navy text-xs font-semibold rounded hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {posting ? 'Posting…' : 'Post comment'}
        </button>
      </div>
    </form>
  )
}
